import { ICommonSettings, IDBConnectionStringSetting } from "@kuika/kuika-cl-model";
import { PureComponent, ReactNode } from "react";
import LocalStorageService from "../../../../../auth/local-storage-service";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigSettingSelect } from "../config-setting-select";
import {
  ConfigDBConnectionStringSettings,
  DB_CONNECTION_STRING_SETTINGS_SEARCH_TERMS
} from "./app-settings-config-db-connection-string";

export interface IConfigDBConnectionStringInputState {
  showModal: boolean;
  editingDBConnectionStringId: string;
}

export interface IConfigDBConnectionStringInputProps {
  parentConfigValue?: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleUpdateCustomFormInput: (formItemName: string[], value: string | null) => void;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
  isWorkflow?: boolean;
  /** Drop the "Default" (platform/preview) option — used where a real setting MUST be picked (WFE download). */
  hideDefaultOption?: boolean;
  /** Rename the null "Default" option (e.g. "Same as Workflow DB") where that reads clearer than "Default". */
  defaultOptionLabel?: string;
  /** When set, the field becomes required and shows this message inline if left empty. */
  requiredMessage?: string;
}

export class ConfigDBConnectionStringInput extends PureComponent<
  IConfigDBConnectionStringInputProps,
  IConfigDBConnectionStringInputState
> {
  formInputName = ["apiConfig", "dbConnectionStringSettingsId"];

  private readonly isOracleManagedDbAndWorkflow: boolean = false;

  constructor(props: IConfigDBConnectionStringInputProps) {
    super(props);
    this.state = {
      showModal: false,
      editingDBConnectionStringId: ""
    };
    if (props.isWorkflow) {
      this.formInputName[1] = "workflowDbConnectionStringSettingsId";
      if (LocalStorageService.getCurrentDomainManagedDbType() === 2) {
        this.isOracleManagedDbAndWorkflow = true;
      }
    }
  }

  openModal = () => {
    this.setState({ showModal: true, editingDBConnectionStringId: "" });
  };

  closeModal = () => {
    this.setState({ showModal: false });
  };

  setEditingDBConnectionString = (id: string) => {
    this.setState({ editingDBConnectionStringId: id, showModal: true });
  };

  handleRestore = (): void => {
    this.props.handleUpdateCustomFormInput?.(this.formInputName, this.props.parentConfigValue ?? null);
  };

  render() {
    const {
      isWorkflow,
      parentConfigValue,
      commonSettings,
      getSectionDescription,
      handleUpdateCustomFormInput,
      handleCreateCommonConfig,
      handleUpdateCommonConfig,
      handleDeleteCommonConfig
    } = this.props;

    const options = [
      ...(this.props.hideDefaultOption
        ? []
        : [{ id: null, name: this.props.defaultOptionLabel ?? "Default" } as unknown as IDBConnectionStringSetting]),
      ...(commonSettings.dbConnectionStringSettings?.filter((config) => config.dbConnectionString) || [])
    ];

    return (
      <>
        <ConfigDBConnectionStringSettings
          isWorkflow={isWorkflow}
          formInputName={this.formInputName}
          showModal={this.state.showModal}
          closeModal={() => {
            this.closeModal();
          }}
          editingDBConnectionStringId={this.state.editingDBConnectionStringId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
        />
        <ConfigSettingSelect
          searchTerms={DB_CONNECTION_STRING_SETTINGS_SEARCH_TERMS}
          label="DB connection string"
          // The workflow-engine download makes this field mandatory: the engine has nowhere
          // to keep its process tables without it, and it cannot be edited after the build.
          rules={this.props.requiredMessage ? [{ required: true, message: this.props.requiredMessage }] : undefined}
          formInputName={this.formInputName}
          options={options}
          tooltipContentKey={
            this.props.isWorkflow
              ? "appsettings_workflow_dbconnectionstring"
              : "appsettings_connectionsettings_dbconnectionstring"
          }
          onAddNew={this.openModal}
          onEdit={this.setEditingDBConnectionString}
          parentConfigValue={this.props.parentConfigValue}
          onRestore={this.handleRestore}
          dropdownTestId="appSettingsDbConnectionStringDropdown"
          addNewTestId="appSettingsDbConnectionStringDropdownAddNew"
          editTestId="appSettingsDbConnectionStringEditButton"
        />
      </>
    );
  }
}
